import { useCallback, useEffect, useState } from "react";
import { useCurrentUser } from "@/lib/auth";
import {
  defaultStatusSichtbarkeit,
  getStatusSichtbarkeit,
  visibleStatusesForUser,
  type StatusSichtbarkeitConfig,
  type StatusSichtbarkeitDomain,
} from "@/lib/statusSichtbarkeit";

let cachedConfig: StatusSichtbarkeitConfig | null = null;
let loadPromise: Promise<StatusSichtbarkeitConfig> | null = null;

export function setStatusSichtbarkeitCache(config: StatusSichtbarkeitConfig | null) {
  cachedConfig = config;
}

async function loadSharedConfig(): Promise<StatusSichtbarkeitConfig> {
  if (cachedConfig) return cachedConfig;
  if (!loadPromise) {
    loadPromise = getStatusSichtbarkeit()
      .then((config) => {
        cachedConfig = config;
        return config;
      })
      .catch(() => defaultStatusSichtbarkeit())
      .finally(() => {
        loadPromise = null;
      });
  }
  return loadPromise;
}

export function useStatusSichtbarkeit() {
  const { user } = useCurrentUser();
  const [config, setConfig] = useState<StatusSichtbarkeitConfig>(
    () => cachedConfig || defaultStatusSichtbarkeit(),
  );
  const [loading, setLoading] = useState(!cachedConfig);

  useEffect(() => {
    let cancelled = false;
    setLoading(!cachedConfig);
    void loadSharedConfig().then((next) => {
      if (!cancelled) {
        setConfig(next);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleStatuses = useCallback(
    (domain: StatusSichtbarkeitDomain): string[] =>
      visibleStatusesForUser(domain, config, user),
    [config, user],
  );

  return { config, loading, user, visibleStatuses };
}
